import { FC, useState } from "react";
import { useTranslation } from "next-i18next";
import { useEffectOnce } from "react-use";
import Image from "next/image";
import Link from "next/link";
import { Article } from "@/lib/types/Article";
import { useAppContext } from "@/src/config/AppProvider";
import useBrowserRoutes from "@/src/utils/helpers/useBrowserRoutes";
import DOMPurify from "isomorphic-dompurify";

const ProgramsSection = () => {
  const { t } = useTranslation();
  const { getLocalization, programs } = useAppContext();

  return (
    <div className="service-wrapper bg-color-white rbt-section-gap">
      <div className="container">
        <div className="row mb--60">
          <div className="col-lg-12">
            <div className="section-title text-center">
              <span className="subtitle bg-pink-opacity">{t("programs")}</span>
              <h2 className="title">{getLocalization("ourProgramsSlogan")}</h2>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-lg-12">
            <div className="container">
              <div className="row row--15 mt_dec--30">
                {programs?.slice(0, 4).map((item, index) => <Program key={item.IdGuid} program={item} index={index} />)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PROGRAM_DETAIL_KEYS = ["firstProgramList", "secondProgramList", "thirdProgramList", "fourProgramList"];

const Program: FC<{ program: Article; index: number }> = ({ program, index }) => {
  const { t } = useTranslation();
  const { getLocalization } = useAppContext();
  const routes = useBrowserRoutes();
  const [sanitizedProgramDetails, setSanitizedProgramDetails] = useState<string>("");
  const [sanitizedProgramContent, setSanitizedProgramContent] = useState<string>("");

  const detailLink = routes.programDetail.replace(":slug", program.Slug);

  useEffectOnce(() => {
    setSanitizedProgramDetails(DOMPurify.sanitize(getLocalization(PROGRAM_DETAIL_KEYS[index])));
    setSanitizedProgramContent(DOMPurify.sanitize(program.Content, { USE_PROFILES: { html: false } }));
  });

  return (
    <div className="col-xl-3 col-md-6 col-sm-6 col-12 mt--30" key={program.IdGuid}>
      <div className="rbt-flipbox variation-2">
        <div className={`rbt-flipbox-wrap rbt-service rbt-service-1 card-bg-${index + 1}`}>
          <div className="rbt-flipbox-front rbt-flipbox-face inner d-flex flex-column flex-fill h-100">
            <div className="front-thumb w-100">
              <Image
                className="w-100 radius-10"
                src={program.ThumbImageUrl}
                width={240}
                height={150}
                alt={program.ImageAltText}
                style={{
                  height: 150,
                  objectFit: "cover"
                }}
              />
            </div>
            <div className="content d-flex flex-column flex-fill">
              <h5 className="title">
                <Link href={detailLink}>{program.Title}</Link>
              </h5>
              <div className="mb--25" dangerouslySetInnerHTML={{ __html: sanitizedProgramDetails }}></div>
              <Link
                className="rbt-btn-link stretched-link"
                href={detailLink}
                style={{
                  marginTop: "auto"
                }}
              >
                {t("programDetails")}
                <i className="feather-arrow-right"></i>
              </Link>
            </div>
          </div>
          <div className="rbt-flipbox-back rbt-flipbox-face inner">
            <div className="flip-back-top">
              <div className="back-thumb w-100">
                <Image
                  className="w-100 radius-10"
                  src={program.ThumbImageUrl}
                  width={240}
                  height={150}
                  alt={program.ImageAltText}
                  style={{
                    height: 150,
                    objectFit: "cover"
                  }}
                />
              </div>
              <p className="text-multiline-ellipsis-6">{sanitizedProgramContent}</p>
            </div>
            <Link className="rbt-btn rbt-switch-btn btn-white btn-sm" href={detailLink}>
              <span data-text={t("programDetails")}>{t("programDetails")}</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgramsSection;
